import {
  Questionnaire,
  QuestionnaireStatus,
} from "@/types/questionnaire/models"
import { useLocation, useNavigate } from "react-router"

import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { useGetQuestionAnsweringTaskGroups } from "@/services/api/questionAnsweringTaskGroupService"
import {
  getRouteByStatus,
  getStatusByRoute,
} from "@/utils/questionnaire/questionnaireStatus"
import { useEffect, useMemo, useState } from "react"

interface QuestionnaireStatusNavigationOptions {
  enableAutoRedirect?: boolean
}

export function useQuestionnaireStatusNavigation(
  questionnaire: Questionnaire | undefined,
  options?: QuestionnaireStatusNavigationOptions,
) {
  const enableAutoRedirect = options?.enableAutoRedirect ?? true
  const [status, setStatus] = useState<QuestionnaireStatus | undefined>(
    questionnaire?.status,
  )
  const featureFlags = useGetAllFeatureFlags()

  const location = useLocation()
  const navigate = useNavigate()

  const taskGroupQueryId = useMemo(() => {
    return enableAutoRedirect ? questionnaire?.id : undefined
  }, [enableAutoRedirect, questionnaire?.id])

  const { data: taskGroups } =
    useGetQuestionAnsweringTaskGroups(taskGroupQueryId)

  useEffect(() => {
    if (!enableAutoRedirect) {
      return
    }

    const expectedStatus = getStatusByRoute(location.pathname)

    if (!questionnaire || !status || status === expectedStatus) {
      return
    }

    let actualRoute
    if (
      status === QuestionnaireStatus.GeneratingAnswers &&
      taskGroups &&
      taskGroups[0]
    ) {
      actualRoute = getRouteByStatus(
        questionnaire.id,
        status,
        questionnaire.fileName,
        questionnaire?.hasPositionData,
        taskGroups[0].id,
        featureFlags,
      )
    } else {
      actualRoute = getRouteByStatus(
        questionnaire.id,
        status,
        questionnaire.fileName,
        questionnaire?.hasPositionData,
        undefined,
        featureFlags,
      )
    }

    navigate(actualRoute)
  }, [
    status,
    location.pathname,
    questionnaire,
    navigate,
    taskGroups,
    enableAutoRedirect,
    featureFlags,
  ])

  return [status, setStatus] as const
}
